import { FormEvent, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/contexts/AuthContext";
import BrandMark from "@/components/brand/BrandMark";
import { ADMIN_ENV, envBadgeLabel } from "@/lib/env";
import { FullPageSpinner } from "@/components/layout/PageSkeleton";
import { cn } from "@/lib/utils";

/**
 * Staff sign-in: Google, or a 6-digit code emailed and typed here.
 * No passwords, no magic links, no phone (founder rule, 26 Sep 2026).
 */

const RESEND_SECONDS = 60;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GoogleG = () => (
  // Google's multi-colour "G" (brand asset for the sign-in button).
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const friendlyError = (message: string): string => {
  const m = message.toLowerCase();
  if (m.includes("rate limit") || m.includes("too many") || m.includes("security purposes"))
    return "Too many codes were asked for. Wait a minute, then try again.";
  if (m.includes("expired") || m.includes("invalid") || m.includes("token"))
    return "That code didn't work. It may be mistyped or expired. Ask for a new one.";
  if (m.includes("provider is not enabled") || m.includes("unsupported provider"))
    return "Google sign-in isn't switched on for this environment yet. Use an email code.";
  if (m.includes("fetch")) return "Can't reach the server. Check your connection and try again.";
  return message;
};

const LoginPage = () => {
  const { session, isLoading, signInWithGoogle, sendEmailCode, verifyEmailCode } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const from: string =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || params.get("next") || "/admin/today";

  const [step, setStep] = useState<"start" | "code">("start");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"google" | "send" | "verify" | null>(null);
  const [cooldown, setCooldown] = useState(0);
  const emailRef = useRef<HTMLInputElement>(null);

  // OAuth errors come back in the URL hash or query.
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const desc = params.get("error_description") || hash.get("error_description");
    if (desc) setError(friendlyError(desc.replace(/\+/g, " ")));
  }, [params]);

  useEffect(() => {
    if (!cooldown) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  if (isLoading && session) return <FullPageSpinner label="Signing you in" />;
  // Signed in: the shell decides between the admin and the "no staff access" page.
  if (session && !isLoading) return <Navigate to={from} replace />;

  const handleGoogle = async () => {
    setError(null);
    setBusy("google");
    try {
      await signInWithGoogle(from !== "/admin/today" ? from : undefined);
    } catch (e) {
      setError(friendlyError((e as Error).message));
      setBusy(null);
    }
  };

  const handleSend = async (e?: FormEvent) => {
    e?.preventDefault();
    setError(null);
    if (!EMAIL_RE.test(email.trim())) {
      setError("Enter the email address of your staff account.");
      emailRef.current?.focus();
      return;
    }
    setBusy("send");
    try {
      await sendEmailCode(email);
    } catch (err) {
      const msg = (err as Error).message || "";
      // Unknown emails get the same screen as known ones (no account probing).
      if (!/signups not allowed|user not found|not allowed for otp/i.test(msg)) {
        setError(friendlyError(msg));
        setBusy(null);
        return;
      }
    }
    setBusy(null);
    setCode("");
    setStep("code");
    setCooldown(RESEND_SECONDS);
  };

  const handleVerify = async (value = code) => {
    if (value.length !== 6) {
      setError("Type all 6 digits from the email.");
      return;
    }
    setError(null);
    setBusy("verify");
    try {
      await verifyEmailCode(email, value);
      navigate(from, { replace: true });
    } catch (err) {
      setError(friendlyError((err as Error).message));
      setCode("");
    } finally {
      setBusy(null);
    }
  };

  return (
    <main className="min-h-screen bg-background flex flex-col">
      <header className="h-16 px-6 flex items-center justify-between">
        <BrandMark showAdminTag />
        <span className="text-xs font-bold tracking-[0.6px] text-ink-3" aria-label={`Environment ${envBadgeLabel(ADMIN_ENV)}`}>
          {envBadgeLabel(ADMIN_ENV)}
        </span>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <section
          aria-labelledby="signin-title"
          className="w-full max-w-[420px] rounded-2xl border bg-card p-8 shadow-[0_8px_24px_rgba(21,32,59,0.08)]"
        >
          {step === "start" ? (
            <>
              <h1 id="signin-title" className="font-serif text-[28px] leading-[34px] font-semibold tracking-[-0.3px]">
                Sign in to the admin
              </h1>
              <p className="mt-2 text-[15px] leading-6 text-ink-2">For Hriatna Eng staff. Readers use the app.</p>

              <Button
                type="button"
                variant="outline"
                onClick={handleGoogle}
                disabled={!!busy}
                className="mt-6 w-full h-12 rounded-[14px] border-[1.5px] border-foreground/80 bg-card text-[15px] font-semibold gap-3"
              >
                {busy === "google" ? <Loader2 className="h-4 w-4 animate-spin" /> : <GoogleG />}
                Continue with Google
              </Button>

              <div className="my-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.8px] text-ink-3">
                <span className="h-px flex-1 bg-border" />
                or
                <span className="h-px flex-1 bg-border" />
              </div>

              <form onSubmit={handleSend} noValidate className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-sm font-semibold">
                    Work email
                  </Label>
                  <Input
                    id="email"
                    ref={emailRef}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    aria-invalid={!!error && step === "start"}
                    aria-describedby={error ? "signin-error" : "email-help"}
                    className="h-12 rounded-xl text-[15px]"
                  />
                  <p id="email-help" className="text-xs text-ink-3">
                    We'll email you a 6-digit code to type here.
                  </p>
                </div>
                <Button type="submit" disabled={!!busy} className="w-full h-12 rounded-[14px] text-[15px] font-semibold gap-2">
                  {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Email me a code
                </Button>
              </form>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => {
                  setStep("start");
                  setError(null);
                }}
                className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-ink hover:underline underline-offset-4"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Use a different email
              </button>
              <h1 id="signin-title" className="font-serif text-[28px] leading-[34px] font-semibold tracking-[-0.3px]">
                Enter your code
              </h1>
              <p className="mt-2 text-[15px] leading-6 text-ink-2">
                If <span className="font-semibold text-foreground break-all">{email.trim()}</span> has a staff account,
                a 6-digit code is on its way. It expires in an hour.
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleVerify();
                }}
                className="mt-6 space-y-4"
              >
                <Label htmlFor="otp" className="sr-only">
                  6-digit code
                </Label>
                <InputOTP
                  id="otp"
                  maxLength={6}
                  value={code}
                  onChange={(v) => {
                    const digits = v.replace(/\D/g, "");
                    setCode(digits);
                    if (digits.length === 6) handleVerify(digits);
                  }}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={busy === "verify"}
                  aria-describedby={error ? "signin-error" : undefined}
                  containerClassName="justify-between"
                >
                  <InputOTPGroup className="gap-2 w-full justify-between">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <InputOTPSlot
                        key={i}
                        index={i}
                        className="h-14 w-12 rounded-xl border text-xl font-semibold first:rounded-l-xl last:rounded-r-xl first:border-l"
                      />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <Button type="submit" disabled={busy === "verify" || code.length !== 6} className="w-full h-12 rounded-[14px] text-[15px] font-semibold">
                  {busy === "verify" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Sign in
                </Button>
              </form>

              <p className="mt-4 text-sm text-ink-2">
                Didn't get it? Check spam, or{" "}
                <button
                  type="button"
                  onClick={() => handleSend()}
                  disabled={cooldown > 0 || !!busy}
                  className={cn(
                    "font-semibold underline-offset-4",
                    cooldown > 0 ? "text-ink-3 cursor-not-allowed" : "text-blue-ink hover:underline"
                  )}
                >
                  {cooldown > 0 ? `send a new code in ${cooldown}s` : "send a new code"}
                </button>
                .
              </p>
            </>
          )}

          {error && (
            <p id="signin-error" role="alert" className="mt-4 rounded-xl bg-[hsl(var(--rose-50))] px-3 py-2 text-sm text-[hsl(var(--rose))]">
              {error}
            </p>
          )}
        </section>
      </div>
    </main>
  );
};

export default LoginPage;
